import React, { PropsWithChildren, useEffect, useMemo } from "react";
import { useEffectOnce } from "usehooks-ts";
import { UnsupportedNetwork } from "../../components/Modal/instance/UnsupportedNetwork/UnsupportedNetwork";
import { useUpdateAddressWithENS } from "../../hooks/useUpdateAddressWithENS";
import { GlobalStore, resetOrderState, resetWrapState } from "../../store/global/GlobalStore";
import { getAllTokenLists } from "../../store/global/getAllTokenLists";
import { OrderStatus } from "../../types/orderStatus";
import { OrderCompleted } from "./screens/OrderCompleted/OrderCompleted";
import { OrderEntry } from "./screens/OrderEntry/OrderEntry";
import { useUrlNetworkParams } from "hooks/useUrlNetworkParams";
import { motion } from "framer-motion";
import { ApprovalsContainer } from "./screens/Approve/ApprovalsContainer";
import { slideRightVariants, slideUpVariants } from "./elements/TransitionAnimations";
import { PropsOf } from "@emotion/react";
import { Helmet } from "react-helmet";
import { Z_INDICES } from "config/consts";
import styled from "@emotion/styled";
import { TradeSurplus } from "./screens/TradeSurplus/TradeSurplus";
import { DeviceType, useDeviceType } from "../../hooks/useDeviceType";
import { calculateUsdTradeSurplus } from "../../utils/orderUtils";
import { WrappingQuote } from "./screens/WrappingQuote/WrappingQuote";
import { DisabledOverlay } from "./elements/DisabledOverlay";

export interface TradeProps {}

export const Trade: React.FC<TradeProps> = () => {
  useUpdateAddressWithENS();
  // Subscribes to network params and updates the store when they change
  useUrlNetworkParams();

  const networkId = GlobalStore.useState(s => s.network.id);
  getAllTokenLists.useBeckon(networkId);

  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo(0, 0);
  }, []);

  useEffectOnce(() => {
    const script = document.createElement("script");
    script.src = "https://bebop.statuspage.io/embed/script.js";
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  });

  const { orderStatus, order, quote } = GlobalStore.useState(s => {
    return {
      orderStatus: s.orderStatus,
      order: s.order,
      quote: s.quote,
    };
  });
  const isTradeSurplus = calculateUsdTradeSurplus(order, quote).value.gt(0.0);
  return (
    <>
      <Helmet>
        <title>Bebop - Trade</title>
        <meta
          name="description"
          content={"Trade all of your favorite token in one go, at best prices."}
        />
        <link rel="canonical" href="https://bebop.xyz/trade" />
      </Helmet>
      <TradeComponents orderStatus={orderStatus} isTradeSurplus={isTradeSurplus} />
      <UnsupportedNetwork />
    </>
  );
};

const TradeComponents: React.FC<{ orderStatus: OrderStatus; isTradeSurplus: boolean }> = React.memo(
  ({ orderStatus, isTradeSurplus }) => {
    const prevOrderStatus = React.useRef(orderStatus);
    const deviceType = useDeviceType();
    useEffect(() => {
      prevOrderStatus.current = orderStatus;
    }, [orderStatus]);

    useEffect(() => {
      return () => {
        //On unmount reset the store
        resetOrderState();

        // Wrap state reset needed temporarily as some of its logic is used in
        // /trade, e.g. in ReviewOrder.tsx: `{wrapQuote?.amount != null &&
        // <WrapActionButton />}`
        resetWrapState();
      };
    }, []);

    const content = useMemo(() => {
      if (
        orderStatus === OrderStatus.ENTRY ||
        orderStatus === OrderStatus.QUOTING ||
        orderStatus === OrderStatus.CONFIRMING ||
        orderStatus === OrderStatus.IN_PROGRESS ||
        orderStatus === OrderStatus.ERROR
      ) {
        return (
          <MotionTrade
            key={1}
            variants={slideUpVariants}
            initial="enter"
            animate="animate"
            exit="exit"
          >
            <OrderEntry />
          </MotionTrade>
        );
      }

      if (orderStatus === OrderStatus.WRAPPING_QUOTE) {
        return (
          <MotionTrade
            key={3}
            variants={slideRightVariants}
            initial="enter"
            animate="animate"
            exit="exit"
          >
            <WrappingQuote />
          </MotionTrade>
        );
      }

      if (orderStatus === OrderStatus.APPROVALS) {
        return (
          <MotionTrade
            key={4}
            variants={slideRightVariants}
            initial="enter"
            animate="animate"
            exit="exit"
          >
            <ApprovalsContainer />
          </MotionTrade>
        );
      }

      if (orderStatus === OrderStatus.TRADE_SURPLUS) {
        return <TradeSurplus />;
      }

      if (orderStatus === OrderStatus.COMPLETED) {
        return <OrderCompleted />;
      }

      throw new Error(`No order status mapping for:${orderStatus}`);
    }, [orderStatus]);

    return (
      <Container deviceType={deviceType} isTradeSurplus={isTradeSurplus}>
        {content}
      </Container>
    );
  }
);

// Sub-components

const Container = styled.div<{ deviceType: DeviceType; isTradeSurplus: boolean }>`
  position: relative;
  z-index: ${Z_INDICES.CONTENT_BASE};
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  perspective: ${p => (p.isTradeSurplus ? "1000px" : "none")};
  margin-right: ${p => {
    // Push the trade container to the right (eq to 1/2 of the width of the navbar)
    // so it stays centered on the page
    switch (p.deviceType) {
      case DeviceType.Desktop:
        return "103px";
      case DeviceType.Tablet:
        return "40px";
      case DeviceType.Mobile:
        return "0px";
    }
  }};
`;

export const MotionTrade = ({
  children,
  variants,
  initial,
  animate,
  exit,
}: PropsWithChildren & PropsOf<typeof motion.div>) => {
  const isChainSwitching = GlobalStore.useState(s => s.isChainSwitching);
  const orderStatus = GlobalStore.useState(s => s.orderStatus);
  const showDisabledOverlay =
    isChainSwitching ||
    orderStatus === OrderStatus.IN_PROGRESS ||
    orderStatus === OrderStatus.CONFIRMING;

  return (
    <MotionTradeContainer
      key={1}
      variants={variants}
      initial={initial}
      animate={animate}
      exit={exit}
    >
      {showDisabledOverlay && (
        <DisabledOverlay
          isProcessingTrade={
            orderStatus === OrderStatus.IN_PROGRESS || orderStatus === OrderStatus.CONFIRMING
          }
        />
      )}
      {children}
    </MotionTradeContainer>
  );
};

const MotionTradeContainer = styled(motion.div)`
  width: 392px;
  padding: 26px 0 28px 0;
  border-radius: 36px;
  background: ${props => props.theme.surface.main.primary.default};
  margin: 32px auto auto;
  z-index: 99;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 32px;
  @media (max-width: 392px) {
    width: 80vw;
    min-width: 340px;
  }
`;
